if (!customElements.get('sticky-site-header')) {
  class StickySiteHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('.section-header') || this.parentElement;
      this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
      this.headerBounds = {};
      this.currentScrollTop = 0;

      this.setHeaderHeight();
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) this.header.classList.add('shopify-section-header-sticky');

      this.onScrollHandler = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScrollHandler, false);

      // The compact sticky state animates the rows' heights, so the measured header height is
      // stale until that finishes. `shopify-section-header-hidden` offsets by --header-height,
      // so re-measure once each height transition lands.
      this.header.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'height') this.setHeaderHeight();
      });

      this.createObserver();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    createObserver() {
      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.querySelector('predictive-search')?.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        requestAnimationFrame(this.reveal.bind(this));
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.querySelectorAll('site-header-dropdown[open]').forEach((dropdown) => dropdown.close());
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }
  }

  customElements.define('sticky-site-header', StickySiteHeader);
}

if (!customElements.get('site-header-dropdown')) {
  class SiteHeaderDropdown extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.site-header__nav-link');
      this.panel = this.querySelector('.site-header__dropdown');
      if (!this.button || !this.panel) return;

      // A parent link renders as <a> when its menu entry points somewhere real (see
      // site-header.liquid), as <button> when it doesn't.
      this.isLink = this.button.tagName === 'A';
      // Hover only drives the panel where hovering actually exists. Touch/coarse pointers keep
      // tap-to-open, so a linked parent still exposes its panel before it can be followed.
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      // Below 990px the nav list is a horizontal scroll container (section-site-header.css), which
      // clips this panel inside the 60px row. position:fixed is the only way out of that clip, and
      // unlike the desktop mega panel there's no height to guess here — the coordinates come off
      // the trigger's live rect at open time.
      this.scrollingNavQuery = window.matchMedia('(max-width: 989px)');

      this.button.addEventListener('click', this.onButtonClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      this.addEventListener('focusin', this.onFocusIn.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPanelReflow = this.onPanelReflow.bind(this);
      this.fitPanelHeight = this.fitPanelHeight.bind(this);

      this.initMegaCategories();
      this.initBrandMasonry();
    }

    // Brand directory "Horizontal" A–Z layout: groups keep A B C… order across each row, but each
    // spans as many tiny grid rows as its own height, so a short letter doesn't leave a gap under
    // it. ResizeObserver also covers the first open (closed panel measures 0) and breakpoint changes.
    initBrandMasonry() {
      const grid = this.querySelector('.mega-brand-dir__columns--horizontal');
      if (!grid || !('ResizeObserver' in window)) return;

      const groups = Array.from(grid.querySelectorAll('.mega-brand-dir__group'));
      const observer = new ResizeObserver(() => {
        const rowHeight = parseFloat(getComputedStyle(grid).gridAutoRows) || 1;
        groups.forEach((group) => {
          const height = group.getBoundingClientRect().height;
          if (height) group.style.gridRowEnd = `span ${Math.ceil(height / rowHeight)}`;
        });
      });

      grid.classList.add('is-masonry');
      groups.forEach((group) => observer.observe(group));
    }

    // Wires the Categories mega menu's tab list (left) to its panel grid (right).
    // Tabs are real <a> links so a no-JS / narrow-viewport visit still navigates;
    // the swap-in-place behavior only kicks in at the desktop breakpoint.
    initMegaCategories() {
      const tabs = Array.from(this.querySelectorAll('.mega-categories__tab'));
      const panels = Array.from(this.querySelectorAll('.mega-categories__panel'));
      if (!tabs.length || !panels.length) return;

      this.megaDesktopQuery = window.matchMedia('(min-width: 990px)');

      this.megaActivate = (index) => {
        tabs.forEach((tab, i) => tab.classList.toggle('is-active', i === index));
        panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('mouseenter', () => {
          if (this.megaDesktopQuery.matches) this.megaActivate(index);
        });
        tab.addEventListener('focus', () => {
          if (this.megaDesktopQuery.matches) this.megaActivate(index);
        });
        tab.addEventListener('click', (event) => {
          if (this.megaDesktopQuery.matches) event.preventDefault();
          this.megaActivate(index);
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      this.releasePanel();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    // Pins the open panel under the nav row in viewport coordinates. Left edge follows the
    // trigger, clamped so a panel hanging off a scrolled-out item stays on screen.
    positionPanel() {
      const trigger = this.button.getBoundingClientRect();
      const row = this.closest('.site-header__nav-row');
      const bottom = row ? row.getBoundingClientRect().bottom : trigger.bottom;
      const gutter = 16;
      const maxLeft = window.innerWidth - this.panel.offsetWidth - gutter;

      this.panel.style.position = 'fixed';
      this.panel.style.top = `${bottom}px`;
      this.panel.style.left = `${Math.max(gutter, Math.min(trigger.left, maxLeft))}px`;
    }

    onPanelReflow() {
      if (!this.hasAttribute('open')) return;
      if (!this.scrollingNavQuery.matches) {
        this.releasePanel();
        return;
      }
      this.positionPanel();
    }

    capturePanel() {
      if (!this.scrollingNavQuery.matches) return;
      this.positionPanel();
      this.navList = this.closest('.site-header__nav');
      window.addEventListener('resize', this.onPanelReflow);
      window.addEventListener('scroll', this.onPanelReflow, { passive: true });
      this.navList?.addEventListener('scroll', this.onPanelReflow, { passive: true });
    }

    releasePanel() {
      if (!this.panel) return;
      window.removeEventListener('resize', this.onPanelReflow);
      window.removeEventListener('scroll', this.onPanelReflow);
      this.navList?.removeEventListener('scroll', this.onPanelReflow);
      this.navList = null;
      this.panel.style.position = '';
      this.panel.style.top = '';
      this.panel.style.left = '';
    }

    onButtonClick(event) {
      if (this.isLink) {
        // On hover devices the panel is already open, so a click just follows the href. On touch
        // the first tap opens the panel and a second tap on the same trigger navigates.
        if (this.hoverQuery.matches || this.hasAttribute('open')) return;
        event.preventDefault();
        this.open();
        return;
      }

      event.preventDefault();
      this.hasAttribute('open') ? this.close() : this.open();
    }

    onMouseEnter() {
      if (!this.hoverQuery.matches) return;
      clearTimeout(this.closeTimer);
      // Short intent delay so sweeping the cursor across the nav to reach a far item doesn't
      // flash every panel it passes over.
      this.openTimer = setTimeout(() => this.open(), 120);
    }

    onMouseLeave() {
      if (!this.hoverQuery.matches) return;
      clearTimeout(this.openTimer);
      // Grace period for the diagonal path from the trigger into the panel.
      this.closeTimer = setTimeout(() => this.close(), 180);
    }

    onFocusIn() {
      clearTimeout(this.closeTimer);
      if (this.hoverQuery.matches) this.open();
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.code === 'Escape') {
        this.close();
        this.button.focus();
        return;
      }

      // A linked parent's Enter belongs to the href, so keyboard users get Arrow Down to open
      // the panel without leaving the page.
      if (event.code === 'ArrowDown' && event.target === this.button) {
        event.preventDefault();
        this.open();
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    open() {
      clearTimeout(this.closeTimer);
      // Re-entering an already-open panel (focusin bubbles from anything inside it) must not
      // reset the mega menu back to its first tab.
      if (this.hasAttribute('open')) return;

      this.closest('.site-header-main')
        ?.querySelectorAll('site-header-dropdown[open]')
        .forEach((dropdown) => dropdown.close());
      this.setAttribute('open', '');
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick);
      if (this.megaActivate) this.megaActivate(0);
      // After the open attribute, so the panel is laid out and its width is measurable.
      this.capturePanel();
      this.fitPanelHeight();
      window.addEventListener('resize', this.fitPanelHeight);
      window.addEventListener('scroll', this.fitPanelHeight, { passive: true });
    }

    // Exposes the room left between the panel's top edge and the bottom of the viewport, so a
    // mega panel taller than the screen (e.g. the Categories columns on a 1024px laptop) can cap
    // its height and scroll inside itself instead of running off the bottom of the page. Re-run
    // on scroll/resize because the sticky header's compact state moves the panel's top edge.
    fitPanelHeight() {
      if (!this.hasAttribute('open')) return;
      const top = Math.max(0, this.panel.getBoundingClientRect().top);
      this.panel.style.setProperty('--site-header-panel-max-h', `${Math.max(240, window.innerHeight - top - 16)}px`);
    }

    close() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.removeAttribute('open');
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.fitPanelHeight);
      window.removeEventListener('scroll', this.fitPanelHeight);
      this.releasePanel();
    }
  }

  customElements.define('site-header-dropdown', SiteHeaderDropdown);
}

if (!customElements.get('site-header-drawer')) {
  class SiteHeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.site-header__drawer');
      this.toggle = this.querySelector('.site-header__burger');
      this.panel = this.querySelector('.site-header__drawer-panel');
      if (!this.drawer || !this.toggle) return;

      this.toggle.addEventListener('click', this.onToggleClick.bind(this));
      this.drawer.querySelectorAll('[data-drawer-close]').forEach((element) => {
        element.addEventListener('click', this.close.bind(this));
      });
      this.onKeydown = this.onKeydown.bind(this);
    }

    onToggleClick() {
      this.drawer.classList.contains('is-open') ? this.close() : this.open();
    }

    open() {
      this.drawer.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel.querySelector('button, a'));
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);
    }

    onKeydown(event) {
      if (event.code === 'Escape') this.close();
    }
  }

  customElements.define('site-header-drawer', SiteHeaderDrawer);
}
